import { AccountCircle, Logout } from '@mui/icons-material';
import { Box, Button, Popover, IconButton, Tooltip } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useOktaAuth } from '@okta/okta-react';
import { ReactElement, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Logo from '../../Assets/KnowRisk_Inline_Dark_RGB.svg';
import './Header.css';
import applicationSettings from '../../Core/Settings/ApplicationSettings';
import { RootState, useApplicationSelector } from '../../Redux/ReduxStore';
import { StoneXDivider } from '../StoneX';
import ChatAssistant from '../MainContainer/CommodityManagement/Dashboard/ChatAssistant';
import { useChatUi } from '../MainContainer/CommodityManagement/Dashboard/ChatUiContext';

interface HeaderProps {
  contextualHeader?: ReactElement;
}

export default function Header(props: HeaderProps) {
  const { isOpen, isFullPage, openChat, closeChat } = useChatUi();

  // The icon toggles: in drawer mode it opens/closes the drawer, in full-page mode it
  // navigates to/from the assistant route.
  const chatActive = isOpen || isFullPage;

  return (
    <header>
      <NavLink to="/">
        <img className="logo" src={Logo}></img>
      </NavLink>
      <div style={{ flexGrow: 1 }}>{props.contextualHeader}</div>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* The assistant is for every signed-in user; the header only renders inside
            AuthWrapper, so being here is the whole access check. */}
        <Tooltip title="Data Assistant">
          <IconButton
            onClick={() => (chatActive ? closeChat() : openChat())}
            sx={{
              color: 'var(--header-icon-color, inherit)',
              // The header is light, so the active and hover states darken rather than
              // lighten - a white wash on a white bar was invisible.
              backgroundColor: chatActive ? 'rgba(0, 0, 0, 0.08)' : undefined,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <ChatIcon />
          </IconButton>
        </Tooltip>
        <UserAccountIcon />
      </Box>
      <ChatAssistant />
    </header>
  );
}

function UserAccountIcon() {
  const email = useApplicationSelector((state: RootState): string[] => state.userAccess.email);
  const displayName = useApplicationSelector((state: RootState): string[] => state.userAccess.displayName);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const { oktaAuth } = useOktaAuth();

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const logOut = async () => await oktaAuth.signOut({ postLogoutRedirectUri: applicationSettings.okta.postLogoutRedirectUri });

  return (
    <>
      <div onClick={handlePopoverOpen} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <AccountCircle />
      </div>
      <Popover
        id="user-account-popoever"
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
      >
        <Box sx={{ margin: '10px', padding: '10px' }}>
          <h2>{displayName}</h2>
          <small className="helper-text">{email}</small>
          <StoneXDivider />
          <Button onClick={logOut} variant="contained" fullWidth startIcon={<Logout />}>
            Logout
          </Button>
        </Box>
      </Popover>
    </>
  );
}
